import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import styled from "@emotion/styled";

const BarContainer = styled(Box)({
  width: "100%",
  height: "40px",
  display: "flex",
  borderRadius: "4px",
  overflow: "hidden",
  marginBottom: "8px",
});

const BarSection = styled(Box)(({ color }) => ({
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: color,
  color: "white",
  fontSize: "0.75rem",
  transition: "all 0.3s ease",
  "&:hover": {
    opacity: 0.8,
  },
}));

const Legend = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "16px",
  marginTop: "8px",
});

const LegendItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const LegendColor = styled(Box)(({ color }) => ({
  width: "12px",
  height: "12px",
  backgroundColor: color,
  borderRadius: "2px",
}));

const StudentRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
  gap: "16px",
});

const StudentName = styled(Typography)({
  minWidth: "120px",
});

const SortButtonGroup = styled(ButtonGroup)({
  marginBottom: "16px",
  display: "flex",
  justifyContent: "center",
  width: "100%",
});

const ControlsContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "24px",
  marginBottom: "16px",
});

const AssignmentStatusBar = ({ students }) => {
  const [sortBy, setSortBy] = useState("good");
  const [includeDropped, setIncludeDropped] = useState(false);

  const colors = {
    goodAssignments: "#ffd700",
    passAssignments: "#4caf50",
    failAssignments: "#f44336",
    missedAssignments: "#9e9e9e",
  };

  const calculatePercentages = (student) => {
    const goodCount = student.goodAssignments?.length || 0;
    const passCount = student.passAssignments?.length || 0;
    const failCount = student.failAssignments?.length || 0;
    const missedCount = student.missedAssignments?.length || 0;

    const total = goodCount + passCount + failCount + missedCount;
    if (total === 0) return null;

    return {
      goodAssignments: (goodCount / total) * 100,
      passAssignments: (passCount / total) * 100,
      failAssignments: (failCount / total) * 100,
      missedAssignments: (missedCount / total) * 100,
    };
  };

  const statusLabels = {
    goodAssignments: "우수",
    passAssignments: "통과",
    failAssignments: "실패",
    missedAssignments: "미제출",
  };

  // 필터링 및 정렬된 학생 목록을 계산
  const filteredAndSortedStudents = useMemo(() => {
    if (!students) return [];

    let filteredStudents = [...students];

    // 탈락자 필터링
    if (!includeDropped) {
      filteredStudents = filteredStudents.filter(
        (student) => student.status === "active"
      );
    }

    // 정렬
    return filteredStudents.sort((a, b) => {
      const aCount =
        a[
          sortBy === "good"
            ? "goodAssignments"
            : sortBy === "fail"
            ? "failAssignments"
            : "missedAssignments"
        ]?.length || 0;
      const bCount =
        b[
          sortBy === "good"
            ? "goodAssignments"
            : sortBy === "fail"
            ? "failAssignments"
            : "missedAssignments"
        ]?.length || 0;
      return bCount - aCount;
    });
  }, [students, sortBy, includeDropped]);

  const renderSection = (percentages, key, counts) => {
    const percentage = percentages[key];
    if (percentage === 0) return null;

    return (
      <Tooltip title={`${statusLabels[key]}: ${counts[key]}개`} key={key}>
        <BarSection color={colors[key]} sx={{ width: `${percentage}%` }}>
          {percentage > 10 && `${percentage.toFixed(1)}%`}
        </BarSection>
      </Tooltip>
    );
  };

  const renderStudentBar = (student) => {
    const counts = {
      goodAssignments: student.goodAssignments?.length || 0,
      passAssignments: student.passAssignments?.length || 0,
      failAssignments: student.failAssignments?.length || 0,
      missedAssignments: student.missedAssignments?.length || 0,
    };

    const percentages = calculatePercentages(student);
    if (!percentages) return null;

    return (
      <StudentRow key={student.userId._id}>
        <StudentName variant="body2">
          {student.userInfo?.name || student.userId.name}
        </StudentName>
        <Box sx={{ flex: 1 }}>
          <BarContainer>
            {renderSection(percentages, "goodAssignments", counts)}
            {renderSection(percentages, "passAssignments", counts)}
            {renderSection(percentages, "failAssignments", counts)}
            {renderSection(percentages, "missedAssignments", counts)}
          </BarContainer>
        </Box>
      </StudentRow>
    );
  };

  if (!students || students.length === 0) {
    return <Typography>데이터가 없습니다</Typography>;
  }

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Legend sx={{ mb: 2 }}>
        {Object.entries(statusLabels).map(([key, label]) => (
          <LegendItem key={key}>
            <LegendColor color={colors[key]} />
            <Typography variant="caption">{label}</Typography>
          </LegendItem>
        ))}
      </Legend>

      <ControlsContainer>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeDropped}
              onChange={(e) => setIncludeDropped(e.target.checked)}
            />
          }
          label="탈락자 포함"
        />
        <SortButtonGroup>
          <Button
            variant={sortBy === "good" ? "contained" : "outlined"}
            onClick={() => setSortBy("good")}
          >
            잘함순
          </Button>
          <Button
            variant={sortBy === "fail" ? "contained" : "outlined"}
            onClick={() => setSortBy("fail")}
          >
            실패순
          </Button>
          <Button
            variant={sortBy === "missed" ? "contained" : "outlined"}
            onClick={() => setSortBy("missed")}
          >
            미제출순
          </Button>
        </SortButtonGroup>
      </ControlsContainer>

      {filteredAndSortedStudents.map(renderStudentBar)}
    </Box>
  );
};

export default AssignmentStatusBar;
